import { sortBy } from "lodash-es";
import { defineStore } from "pinia";

import { getStickies } from "@/backend/read/BackendReader";
import { addSticky } from "@/backend/read/changeSticky";
import type { IdMap } from "@/model/baseTypes";
import type { Board, BoardWithObjectives } from "@/model/board";
import { isBacklogBoard } from "@/model/board";
import type { BoardCard, Card, LinkedSticky } from "@/model/card";
import type {
  Link,
  LinkType,
  LinkableCard,
  LinkableItem,
  Linking,
  LinkingCard,
  LinkingTarget,
  ObjectiveLink,
} from "@/model/link";
import type { Objective } from "@/model/objective";
import { isEmpty } from "@/utils/object";

import { useBoardStore } from "./board";
import { useBoardsStore } from "./boards";
import { useCardStore } from "./card";
import { calcLinkState } from "./helpers/linkState";
import { fadeOutCard, markLinkedCard, resetAllCardMarks } from "./helpers/mark";
import { useObjectiveStore } from "./objective";
import { useSessionStore } from "./session";
import { useTeamStore } from "./team";

export interface LinkedCards {
  link: Link;
  from: BoardCard;
  to: BoardCard;
}

export const useLinkStore = defineStore("link", {
  state: () => ({
    links: [] as Link[],
    linksById: {} as Record<Link["id"], Link>,
    linksByFrom: {} as Record<Link["from"], Link[]>,
    linksByTo: {} as Record<Link["to"], Link[]>,
    markingObjectiveLinkedCards: null as Objective["id"] | null,
    markingCardsLinkedCards: null as BoardCard[] | null,
    linking: { from: null, to: null } as Linking,
    linkTypes: [] as LinkType[],
    linkGroups: {} as Record<string, IdMap<BoardCard>>,
  }),
  getters: {
    linksByCard: (state) => {
      return (lnkId: string): Link[] => [
        ...(state.linksByFrom[lnkId] || []),
        ...(state.linksByTo[lnkId] || []),
      ];
    },
    isLinkedFrom: (state) => {
      return (id: LinkingTarget["id"]) => state.linking.from?.id === id;
    },
    isPinned: (state) => {
      return (cardId: string) =>
        state.markingCardsLinkedCards?.some((card) => card.data.id === cardId) ?? false;
    },
    isMarkingLinks: (state) =>
      !!state.markingObjectiveLinkedCards ||
      (state.markingCardsLinkedCards !== null && state.markingCardsLinkedCards.length > 0),
    cardsByLink:
      (state) =>
      (linkTargetId: string): Card[] => {
        const groups = state.linkGroups[linkTargetId];
        if (groups) {
          const res = [];
          for (const id in groups) {
            res.push(groups[id].data);
          }
          return res;
        }
        const card = useCardStore().cards[linkTargetId];
        if (!card) {
          // can happen because of REN-8846
          return [];
        }
        return [card];
      },
    boardCardByLink:
      (state) =>
      (linkTargetId: string, board: Board): BoardCard | undefined => {
        const groups = state.linkGroups[linkTargetId];
        return groups ? groups[board.id] : board.cards[linkTargetId];
      },
    getLinkedCards:
      (state) =>
      (linkingTarget: LinkingTarget, withLinks = false): Card[] => {
        const missingCards: string[] = [];

        const resolveCards = (ids: string[]) =>
          ids
            .map((id) => {
              const card = getCardByLinkId(id);
              if (!card) {
                // Assume the card is from another board
                missingCards.push(id);
                return null;
              }
              return card;
            })
            .filter((c): c is Card => !!c)
            .reverse();

        let cardLinks: Card[];

        if (linkingTarget.type === "sticky") {
          const linkedStickies = useCardStore().cards[linkingTarget.id]?.linkedStickies;
          if (!linkedStickies) return [];
          cardLinks = resolveCards(linkedStickies.map((l) => l.stickyId));
        } else {
          const linkedStickies = useObjectiveStore().objectiveById(linkingTarget.id, {
            boardId: linkingTarget.boardId,
          })?.cards;
          if (!linkedStickies) return [];
          cardLinks = resolveCards(linkedStickies.filter((c) => c.isOrigin).map((c) => c.id));
        }

        if (missingCards.length) loadStickies(missingCards);

        return cardLinks;

        // Load the missing stickies. This will add them to the store,
        // and Vue will re-call this function to render the full list.
        function loadStickies(ids: string[]) {
          void getStickies(ids, withLinks).then((stickies) => stickies.forEach(addSticky));
        }

        function getCardByLinkId(linkId: string) {
          const groups = state.linkGroups[linkId];
          if (groups && !isEmpty(groups)) {
            return Object.entries(groups)[0][1].data;
          }
          const card = useCardStore().cards[linkId];
          if (!card) {
            // can happen because of REN-8846
            return;
          }
          return card;
        }
      },
    getLinkedObjectives:
      () =>
      (linkingCard: LinkingCard): LinkableItem[] => {
        const card = useCardStore().cards[linkingCard.id];
        if (!card) return [];

        return card.objectives
          .flatMap((objective) => {
            const board = useBoardsStore().boards[objective.boardId] as BoardWithObjectives;
            const boardObjective = [
              ...(board?.objectives ?? []),
              ...(board?.stretchObjectives ?? []),
            ].find(({ id }) => objective.id === id);
            if (!boardObjective) {
              return [];
            }

            return {
              kind: "objective" as const,
              linked: true,
              id: objective.id,
              text: boardObjective.text,
              description: boardObjective.description,
              boardId: objective.boardId,
              type: useObjectiveStore().objectiveTypeById(objective.id, {
                boardId: objective.boardId,
              }),
            };
          })
          .reverse();
      },
    getLinkedCardItems: function () {
      return (linkingTarget: LinkingTarget, withLinks: boolean): LinkableCard[] => {
        const cards = this.getLinkedCards(linkingTarget, withLinks);
        return cards.map((linkedCard: Card) => linkableCardOf(linkedCard, true));
      };
    },
    getLinkedItems() {
      return (linkingTarget: LinkingTarget, withLinks = false): LinkableItem[] => [
        ...this.getLinkedCardItems(linkingTarget, withLinks),
        ...(linkingTarget.type === "sticky" ? this.getLinkedObjectives(linkingTarget) : []),
      ];
    },
    linksOnBoard() {
      const linkMap = new Map<string, LinkedCards>();
      const board = useBoardStore().board;
      if (!board) {
        return [];
      }
      Object.values(board.cards).flatMap((from) => {
        for (const link of from.data.linkedStickies) {
          if (link.direction === "from") {
            const to = this.boardCardByLink(link.stickyId, board);
            if (to) {
              linkMap.set(link.linkId, {
                link: { id: link.linkId, from: from.data.id, to: link.stickyId, state: link.state },
                from,
                to,
              });
            }
          }
        }
      });
      return [...linkMap.values()];
    },
    riskyLinkCount() {
      const counts = { risky: 0, critical: 0 };
      this.linksOnBoard.forEach((linked) => {
        if (linked.link.state === "warn") {
          counts.risky++;
        }
        if (linked.link.state === "error") {
          counts.critical++;
        }
      });
      return counts;
    },
    hasRiskyLinks() {
      const counts: { risky: number; critical: number } = this.riskyLinkCount;
      return counts.risky > 0 || counts.critical > 0;
    },
  },
  actions: {
    setLinks(links: Link[]) {
      this.links = [];
      for (const link of links) {
        this.addLink(link);
      }
    },
    addLink(link: Link) {
      this.links.push(link);
      this.linksById[link.id] = link;
      add(this.linksByFrom, link.from, link);
      add(this.linksByTo, link.to, link);

      function add(map: Record<string, Link[]>, id: string, link: Link) {
        if (!(id in map)) {
          map[id] = [];
        }
        map[id].push(link);
      }
    },
    addToLinkGroup(groupId: string, boardId: string, boardCard: BoardCard) {
      if (!this.linkGroups[groupId]) this.linkGroups[groupId] = {};
      this.linkGroups[groupId][boardId] = boardCard;
    },
    removeFromLinkGroup(card: Card, boardId: string) {
      if (card.groupId && this.linkGroups[card.groupId]) {
        delete this.linkGroups[card.groupId][boardId];
      }
    },
    removeLink(index: number) {
      const link = this.links[index];
      this.links.splice(index, 1);
      delete this.linksById[link.id];
      remove(this.linksByFrom, link.from, link);
      remove(this.linksByTo, link.to, link);
      return link;

      function remove(map: Record<string, Link[]>, id: string, link: Link) {
        const i = map[id].findIndex((l) => l.id === link.id);
        if (i >= 0) {
          map[id].splice(i, 1);
        }
      }
    },

    add(link: Link) {
      if (this.linksById[link.id]) {
        return;
      }
      this.addLink(link);
    },

    remove(linkId: string) {
      const linkIndex = this.links.findIndex((link) => link.id === linkId);
      if (linkIndex < 0) {
        return;
      }
      this.removeLink(linkIndex);
    },
    addObjectiveLink(cardId: Card["id"], linkInfo: { boardId: string; objectiveId: string }) {
      const card = useCardStore().cards[cardId];
      if (!card) {
        return;
      }
      if (
        card.objectives.every(
          (cardObjective) =>
            cardObjective.boardId !== linkInfo.boardId || cardObjective.id !== linkInfo.objectiveId,
        )
      ) {
        card.objectives.push({ id: linkInfo.objectiveId, boardId: linkInfo.boardId });
      }
    },
    removeObjectiveLink(
      cardId: Card["id"],
      linkInfo: Pick<ObjectiveLink, "objectiveId" | "boardId">,
    ) {
      const card = useCardStore().cards[cardId];
      if (!card) {
        return;
      }
      card.objectives = card.objectives.filter(
        (cardObjective) =>
          cardObjective.boardId !== linkInfo.boardId || cardObjective.id !== linkInfo.objectiveId,
      );
    },
    setLinkState(card: Card, link: LinkedSticky) {
      const card1 = this.cardsByLink(card.id)[0];
      const card2 = this.cardsByLink(link.stickyId)[0];
      if (card1 && card2) {
        link.state = calcLinkState(card1, card2);
      }
    },
    setCardLinkStates(card: Card) {
      card.linkedStickies.forEach((link) => this.setLinkState(card, link));
    },
    updateCardLinkedMarks(): string[] {
      const cards = this.markingCardsLinkedCards;
      return cards && cards.length > 0 ? this.markCardLinkedCards(cards) : [];
    },
    markCardLinkedCards(cards: BoardCard[]): string[] {
      this.markCardsLinkedCards(cards);
      const distances = this.linkedCardDistances(cards);
      const highlighted = new Array<{ dist: number; id: string }>();
      Object.values(useBoardStore().currentBoard().cards).forEach((boardCard) => {
        const id = boardCard.data.id;
        if (markLinkedCard(boardCard, distances[id])) {
          highlighted.push({ dist: distances[id], id });
        }
      });
      return sortBy(highlighted, (h) => -h.dist).map((h) => h.id);
    },

    markCardsLinkedCards(cards: BoardCard[]) {
      this.markingObjectiveLinkedCards = null;
      this.markingCardsLinkedCards = cards;
    },

    // breadth first search from given cards following all links
    linkedCardDistances(cards: BoardCard[]): IdMap<number> {
      const boardCardByLink = this.boardCardByLink;

      const queue = cards.map((card) => ({ card, distance: 0 }));
      const linked: IdMap<number> = {};
      while (queue.length > 0) {
        const [item] = queue.splice(0, 1);
        if (!(item.card.data.id in linked)) {
          linked[item.card.data.id] = item.distance;
          item.card.data.linkedStickies.forEach((link) => {
            addToQueue(link.stickyId, item.distance + 1);
          });
        }
      }
      return linked;

      function addToQueue(linkTargetId: string, distance: number) {
        const card = boardCardByLink(linkTargetId, useBoardStore().currentBoard());
        if (card && !(card.data.id in linked)) {
          queue.push({ card, distance });
        }
      }
    },

    markObjectiveLinkedCards(objectiveId: string) {
      this.markingCardsLinkedCards = null;
      this.markingObjectiveLinkedCards = objectiveId;

      Object.values(useBoardStore().currentBoard().cards).forEach((boardCard) => {
        const isLinkedToObjective = boardCard.data.objectives.some(
          (objective) => objective.id === objectiveId,
        );

        const isLinkedToBacklogItem = boardCard.data.linkedStickies.some((link) => {
          const linkedCard = useCardStore().cards[link.stickyId];

          return (
            isBacklogBoard(linkedCard?.type.origin) &&
            linkedCard.objectives.some((objective) => objective.id === objectiveId)
          );
        });

        fadeOutCard(boardCard, !isLinkedToObjective && !isLinkedToBacklogItem);
      });
    },
    removeAllMarks() {
      this.markingObjectiveLinkedCards = null;
      this.markingCardsLinkedCards = null;
      resetAllCardMarks(useBoardStore().currentBoard());
    },
    removeCardLinkedMarks(cardId: string) {
      if (this.markingCardsLinkedCards?.some((card) => card.data.id === cardId)) {
        this.markingCardsLinkedCards = null;
        resetAllCardMarks(useBoardStore().currentBoard());
      }
    },
    removeObjectiveLinkedMarks(objectiveId: string) {
      if (this.markingObjectiveLinkedCards === objectiveId) {
        this.markingObjectiveLinkedCards = null;
        resetAllCardMarks(useBoardStore().currentBoard());
      }
    },

    setLinkingTarget(target: LinkingTarget) {
      this.linking.to = target;
    },
    resetLinkingTarget() {
      this.linking.to = null;
    },
  },
});

export function linkId(
  card: Pick<Card, "id" | "groupId">,
): NonNullable<Card["groupId"] | Card["id"]> {
  return card.groupId || card.id;
}

export function linkBetween(card: Card, other?: Pick<Card, "id" | "groupId"> | null) {
  return (
    other && card.id !== other.id && card.linkedStickies.find((l) => l.stickyId === linkId(other))
  );
}

export function getLinkTargetId(card: Card, link: Link): string {
  return linkId(card) === link.from ? link.to : link.from;
}

export function linkableCardOf(card: Card, linked: boolean): LinkableCard {
  return {
    kind: "sticky",
    linked,
    id: card.id,
    groupId: card.groupId ?? undefined,
    text: card.text,
    type: card.type,
    flagType: card.flagType,
    iteration: useSessionStore().getIterationById(card.iterationId),
    team: card.teamId ? useTeamStore().findTeam({ id: card.teamId }) : undefined,
    artId: card.artId ?? undefined,
    almId: card.almId,
    status: card.status,
    points: card.points,
    precondTeam: card.precondTeam ?? undefined,
    dependTeam: card.dependTeam ?? undefined,
  };
}
